import type { FormEvent, ReactNode } from 'react'
import type { PaymentMethod } from '@/types/clinic'

import { useState } from 'react'
import { toast } from 'sonner'
import { useClinic } from '@/lib/clinic/store'
import { roundMoney } from '@/lib/format'
import { getInvoiceSummary } from '@/lib/clinic/selectors'
import { useMoney } from '@/hooks/use-clinic-helpers'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import FormField from '@/components/app/form-field'
import SimpleSelect from '@/components/app/simple-select'

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

import { PAYMENT_METHOD_LABELS } from '@/constants/clinic'

export interface PaymentDialogOptions {
    invoiceId: string
}

interface PaymentDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    options: PaymentDialogOptions
}

const PaymentDialog = ({ open, onOpenChange, options }: PaymentDialogProps): ReactNode => {
    const { data, today, actions } = useClinic()
    const money = useMoney()
    const invoice = data.invoices.find(item => item.id === options.invoiceId)
    const summary = invoice ? getInvoiceSummary(invoice) : null
    const patient = invoice ? data.patients.find(item => item.id === invoice.patientId) : undefined
    const methods = data.settings.billing.paymentMethods.length ? data.settings.billing.paymentMethods : (['cash'] as PaymentMethod[])

    const [amount, setAmount] = useState(() => String(summary?.balance ?? ''))
    const [date, setDate] = useState(today)
    const [method, setMethod] = useState<PaymentMethod>(methods.includes('upi') ? 'upi' : methods[0])
    const [reference, setReference] = useState('')
    const [note, setNote] = useState('')
    const [submitted, setSubmitted] = useState(false)

    const value = Number(amount)
    const balance = summary?.balance ?? 0
    const amountError = !amount || Number.isNaN(value) || value <= 0
        ? 'Enter an amount greater than zero.'
        : value > balance + 0.001 ? `The balance is only ${money(balance)}.` : ''
    const dateError = !date ? 'Choose the payment date.' : ''

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault()
        setSubmitted(true)
        if (!invoice || amountError || dateError) return
        actions.addPayment(invoice.id, { date, amount: roundMoney(value), method, reference: reference.trim(), note: note.trim() })
        const remaining = roundMoney(balance - value)
        toast.success(`Payment of ${money(value)} recorded`, {
            description: remaining > 0 ? `${money(remaining)} still pending on ${invoice.number}` : `${invoice.number} is now fully paid`
        })
        onOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='sm:max-w-md'>
                <form onSubmit={handleSubmit} noValidate className='grid gap-5'>
                    <DialogHeader>
                        <DialogTitle>Record payment</DialogTitle>
                        <DialogDescription>
                            {invoice && summary ? `${invoice.number} for ${patient?.name ?? 'patient'}. Balance due ${money(summary.balance)}.` : 'This bill is no longer available.'}
                        </DialogDescription>
                    </DialogHeader>

                    {invoice && summary && (
                        <div className='grid grid-cols-3 gap-2 rounded-lg bg-muted/50 p-3 text-center'>
                            <div>
                                <p className='text-xs text-muted-foreground'>Total</p>
                                <p className='text-sm font-semibold'>{money(summary.total)}</p>
                            </div>
                            <div>
                                <p className='text-xs text-muted-foreground'>Paid</p>
                                <p className='text-sm font-semibold text-emerald-700'>{money(summary.paid)}</p>
                            </div>
                            <div>
                                <p className='text-xs text-muted-foreground'>Balance</p>
                                <p className='text-sm font-semibold text-rose-700'>{money(summary.balance)}</p>
                            </div>
                        </div>
                    )}

                    <div className='grid gap-4 sm:grid-cols-2'>
                        <FormField label='Amount' htmlFor='payment-amount' required error={submitted ? amountError : ''}>
                            <Input
                                id='payment-amount'
                                type='number'
                                inputMode='decimal'
                                min={0}
                                step='1'
                                value={amount}
                                onChange={event => setAmount(event.target.value)}
                                aria-invalid={submitted && Boolean(amountError)}
                            />
                        </FormField>

                        <FormField label='Payment date' htmlFor='payment-date' required error={submitted ? dateError : ''}>
                            <Input id='payment-date' type='date' value={date} max={today} onChange={event => setDate(event.target.value)} />
                        </FormField>

                        <FormField label='Payment method' htmlFor='payment-method'>
                            <SimpleSelect
                                id='payment-method'
                                value={method}
                                onValueChange={next => setMethod(next as PaymentMethod)}
                                options={methods.map(item => ({ value: item, label: PAYMENT_METHOD_LABELS[item] }))}
                            />
                        </FormField>

                        <FormField label='Reference' htmlFor='payment-reference' hint='UPI or card reference, optional'>
                            <Input id='payment-reference' value={reference} onChange={event => setReference(event.target.value)} placeholder='UPI ref 4521' />
                        </FormField>

                        <FormField label='Note' htmlFor='payment-note' className='sm:col-span-2'>
                            <Input id='payment-note' value={note} onChange={event => setNote(event.target.value)} placeholder='Balance after crown fitting' />
                        </FormField>
                    </div>

                    <DialogFooter>
                        <DialogClose render={<Button type='button' variant='outline' />}>
                            Cancel
                        </DialogClose>
                        <Button type='submit' disabled={!invoice || balance <= 0}>
                            Record payment
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export default PaymentDialog
